/*
 * Keeps the theme's "Menge" stepper showing the quantity the Kaching Bundles
 * block is actually going to add.
 *
 * Why this is needed
 * ------------------
 * Kaching renders its deal bars client-side and, when one is selected, injects
 * its OWN <input name="quantity"> into the theme's product form, carrying the
 * deal quantity (Kauf 2 -> 2, Kauf 4 -> 4). The theme's visible stepper is a
 * second input of the same name in that same form, and it stays at 1.
 *
 * So the form posts `quantity` twice. /cart/add takes the last one, which is
 * Kaching's — the right number reaches the cart, but the stepper on screen says
 * 1 the whole time, and nudging it with the +/- buttons changes nothing the
 * customer can see in the cart. That is the reported bug: "whichever you press,
 * nothing happens".
 *
 * What this does
 * --------------
 * Mirrors the two inputs in both directions, so the number on screen is always
 * the number that gets added:
 *
 *   deal selected  -> copy Kaching's quantity into the visible stepper
 *   stepper used   -> copy the visible quantity into Kaching's hidden input
 *
 * Note the safety property: once the two agree, it no longer matters which of
 * the duplicate `quantity` fields /cart/add decides to honour. This deliberately
 * does NOT add a third input or touch the submit path — the app keeps owning
 * what is added, this only stops the theme from lying about it.
 *
 * Kaching's DOM is app-owned and can be renamed without notice, so every lookup
 * is defensive: nothing here throws if the block is absent (most products have
 * no deal) or if a class changes — the stepper simply behaves as it does today.
 */
(function () {
  'use strict';

  var KACHING_BLOCK = '.kaching-bundles__block';
  var DEAL_RADIO = 'input[type="radio"][name^="kaching-bundles-deal"]';

  /* The theme's stepper is the quantity input tied to the product form through a
     form="" attribute — that is what distinguishes it from Kaching's, which is a
     plain hidden input parked inside the form itself. */
  function themeInput() {
    return document.querySelector('input[name="quantity"][form]');
  }

  function kachingInput() {
    var inputs = document.querySelectorAll('form[action*="/cart/add"] input[name="quantity"]');
    for (var i = 0; i < inputs.length; i++) {
      if (!inputs[i].getAttribute('form') && inputs[i].type === 'hidden') return inputs[i];
    }
    return null;
  }

  /* Guard flag rather than removing/re-adding listeners: writing to one input
     fires the change event the other side listens for, which would bounce back
     and forth. */
  var syncing = false;

  function copyToTheme() {
    var from = kachingInput();
    var to = themeInput();
    if (!from || !to || syncing) return;

    var value = parseInt(from.value, 10);
    if (!value || value < 1 || String(value) === to.value) return;

    syncing = true;
    to.value = value;
    /* bubbles, so <quantity-set> re-runs validateQtyRules and re-enables the
       minus button that it disables at the minimum. */
    to.dispatchEvent(new Event('change', { bubbles: true }));
    syncing = false;
  }

  /* Kaching rewrites its hidden input from its own handler for the same change
     event, and not necessarily before ours runs — measured on the live page, a
     single next-frame read still got the previous value. Rather than guess at
     the app's timing, re-read over a short window; copyToTheme is a no-op once
     the two agree, so the extra passes cost nothing and stop as soon as the new
     value lands. */
  var POLL_DELAYS = [0, 50, 120, 250, 450, 800];

  function pollCopyToTheme() {
    for (var i = 0; i < POLL_DELAYS.length; i++) {
      setTimeout(copyToTheme, POLL_DELAYS[i]);
    }
  }

  function copyToKaching() {
    var from = themeInput();
    var to = kachingInput();
    if (!from || !to || syncing) return;

    var value = parseInt(from.value, 10);
    if (!value || value < 1 || String(value) === to.value) return;

    syncing = true;
    to.value = value;
    syncing = false;
  }

  function start() {
    var block = document.querySelector(KACHING_BLOCK);
    if (!block) return false;

    /* Delegated: the bars are re-rendered whenever the variant changes, so a
       listener bound to today's radios would be lost. */
    document.addEventListener('change', function (event) {
      var target = event.target;
      if (!target || !target.matches) return;

      if (target.matches(DEAL_RADIO)) {
        pollCopyToTheme();
        return;
      }

      if (target === themeInput()) copyToKaching();
    });

    /* The block renders with a deal preselected (and A/B tests change WHICH
       one), so sync once on load without waiting for an interaction. */
    pollCopyToTheme();
    return true;
  }

  /* The block is injected by the app's script, so it is not in the DOM at
     DOMContentLoaded. Watch for it, and stop watching once it lands — capped so
     a product without any deal does not observe forever. */
  function init() {
    if (start()) return;

    var observer = new MutationObserver(function () {
      if (start()) observer.disconnect();
    });
    observer.observe(document.body, { childList: true, subtree: true });
    setTimeout(function () {
      observer.disconnect();
    }, 15000);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
